import React, { ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Styles } from 'jss';
import mapValues from 'lodash/mapValues';
import { Chip, ChipColor } from '@app/core';

const useStyles = createUseStyles((theme: Theme) => ({
  icons: {
    fontSize: theme.iconSize.small,
    fontWeight: 500,
  },
  container: {
    display: 'inline-flex',
    justifyContent: 'center',
    gap: theme.marginBase,
    position: 'relative',
  },
  noText: {
    minWidth: theme.height.chips,
    justifyContent: 'center',
    padding: 0,
  },
}));

const usePlainColorStyle = createUseStyles((theme: Theme): Styles<Colors> => {
  return mapValues(theme.colorDefinitions, (def) => ({
    backgroundColor: def.accessible,
    color: theme.colors.white,
  }));
});

export interface IconChipsProps {
  label?: ReactNode;
  color?: ChipColor;
  icon?: string;
  iconClassName?: string;
  className?: string;
  variant?: 'plain' | 'std';
  children?: ReactNode;
  overGray?: boolean;
  alt?: string;
}

export const IconChip: React.FC<IconChipsProps> = ({
  label,
  color = 'blue',
  icon,
  iconClassName,
  variant = 'std',
  children,
  className,
  alt,
  overGray,
}) => {
  const classes = useStyles();
  const plainStyles = usePlainColorStyle();

  return (
    <Chip
      color={color}
      overGray={overGray}
      className={classnames(
        classes.container,
        {
          [classes.noText]: icon && !label && !children,
          [plainStyles[color]]: variant === 'plain',
        },
        className,
      )}
    >
      {icon && (
        <i
          className={classnames(icon, classnames(classes.icons, iconClassName))}
          aria-label={alt}
          title={alt}
        />
      )}
      {label && <div>{label}</div>}
      {children}
    </Chip>
  );
};
